import { Autocomplete, Box, darken, styled, ToggleButton, ToggleButtonGroup, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { AppTextField } from 'src/components/AppTextField'
import { CommonPageLayout } from 'src/components/common/CommonPageLayout'
import { GlobalDocumentType } from 'src/enums'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { IPaginatedResult } from '../../../api/paginated-result'
import { IBaseSupplier, ICustomerAdminSupplier, ISupplier } from '../../../generated-types'
import { GlobalDocumentsTable } from './GlobalDocumentsTable'

export interface GlobalDocumentContainerProps {
  adminSuppliers?: IPaginatedResult<ISupplier> | undefined
  customerSuppliers?: IPaginatedResult<ICustomerAdminSupplier> | undefined
  isSuperAdmin?: boolean
  globalDocumentType?: GlobalDocumentType
}

export const GlobalDocumentContainer: React.FC<GlobalDocumentContainerProps> = ({
  adminSuppliers,
  customerSuppliers,
  isSuperAdmin = false,
  globalDocumentType,
}) => {
  const { t: translate } = useTranslation(['global-documents', 'common'])
  const theme = useTheme()
  const supplier = useMemo(() => {
    const supplierDataFromSession = sessionStorage.getItem(isSuperAdmin ? 'admin-supplier' : 'customer-supplier')
    if (supplierDataFromSession) {
      return JSON.parse(supplierDataFromSession) as IBaseSupplier
    }
  }, [isSuperAdmin])
  const { id: customerId } = useParams()
  const suppliers = customerId ? customerSuppliers : adminSuppliers
  const [selectedSupplier, setSelectedSupplier] = useState<IBaseSupplier | null>(supplier as IBaseSupplier)
  const [globalDocumentTypeState, setGlobalDocumentTypeState] = useState<GlobalDocumentType | undefined>(
    globalDocumentType,
  )

  useEffect(() => {
    setGlobalDocumentTypeState(globalDocumentType)
  }, [globalDocumentType])

  const handleSaveLocal = useCallback(() => {
    if (selectedSupplier != null) {
      sessionStorage.setItem(
        isSuperAdmin ? 'admin-supplier' : 'customer-supplier',
        JSON.stringify({ id: selectedSupplier.id, name: selectedSupplier.name }),
      )
    } else {
      sessionStorage.removeItem(isSuperAdmin ? 'admin-supplier' : 'customer-supplier')
    }
  }, [isSuperAdmin, selectedSupplier])

  window.onbeforeunload = useCallback(() => {
    handleSaveLocal()
  }, [handleSaveLocal]) //this use to save locally when refresh

  useEffect(() => {
    if (supplier) {
      setSelectedSupplier(supplier as IBaseSupplier)
    }
  }, [supplier])

  useEffect(() => {
    return () => {
      handleSaveLocal()
    }
  }, [handleSaveLocal]) //this use to save locally when unmount

  const handleSelect = (_, value) => {
    setSelectedSupplier(value)
  }

  const handleDocumentTypeChange = (_, value: GlobalDocumentType | null) => {
    if (value !== null) {
      setGlobalDocumentTypeState(value)
    }
  }

  useEffect(() => {
    if (!selectedSupplier || !suppliers?.items?.length) return
    const currentSupplier = (suppliers.items as (ICustomerAdminSupplier | ISupplier)[]).find(
      x => x.id === selectedSupplier.id,
    )
    if (currentSupplier?.isDeleted && !currentSupplier?.hasRelatedDocuments) {
      setSelectedSupplier(null)
    }
  }, [suppliers, selectedSupplier])

  return (
    <CommonPageLayout
      titleSection={{ title: translate('global-documents', { ns: 'global-documents' }) }}
      topAction={[
        <Box key="top-actions" display="flex" alignItems="center" gap="16px" height="54px" marginBottom={1}>
          {globalDocumentTypeState !== GlobalDocumentType.Locality &&
            globalDocumentTypeState !== GlobalDocumentType.AdminLocality && (
              <Autocomplete
                key="supplier"
                sx={{
                  width: '20vw',
                  backgroundColor: 'white',
                  height: '48px',
                  boxSizing: 'border-box',
                  '& .MuiOutlinedInput-root': { height: '48px' },
                }}
                value={selectedSupplier}
                clearOnEscape
                openOnFocus={true}
                onChange={handleSelect}
                disablePortal
                id="combo-box-demo"
                options={
                  (suppliers?.items as (ICustomerAdminSupplier | ISupplier)[])?.filter(
                    (supplier: ICustomerAdminSupplier | ISupplier) =>
                      !(supplier.isDeleted && !supplier.hasRelatedDocuments),
                  ) || []
                }
                getOptionLabel={supplier => `${supplier.name}`}
                renderInput={params => (
                  <AppTextField
                    {...params}
                    label={translate('supplier', { ns: 'global-documents' })}
                    inputProps={{
                      ...params.inputProps,
                    }}
                    sx={{ paddingY: 0, paddingX: 0 }}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            )}
          {isSuperAdmin && (
            <StyledToggleButtonGroup
              key="document-type"
              exclusive
              size="small"
              value={globalDocumentTypeState}
              onChange={handleDocumentTypeChange}
              sx={{ height: '50px' }}
            >
              <ToggleButton value={GlobalDocumentType.Equipment}>
                {translate('equipment', { ns: 'global-documents' })}
              </ToggleButton>
              <ToggleButton value={GlobalDocumentType.AdminLocality}>
                {translate('locality', { ns: 'global-documents' })}
              </ToggleButton>
            </StyledToggleButtonGroup>
          )}
        </Box>,
      ]}
    >
      <Box padding={'8px'} bgcolor={getThemeColor(theme, MUIThemeColors.white)} borderRadius={'4px'}>
        <GlobalDocumentsTable
          isSuperAdmin={isSuperAdmin}
          customerId={customerId}
          supplierId={
            globalDocumentTypeState !== GlobalDocumentType.Locality &&
            globalDocumentTypeState !== GlobalDocumentType.AdminLocality &&
            selectedSupplier
              ? (selectedSupplier?.id as number)
              : undefined
          }
          height={window.innerHeight - 320 + 'px'}
          isDeletedSupplier={
            globalDocumentTypeState === GlobalDocumentType.Locality ||
            globalDocumentTypeState === GlobalDocumentType.AdminLocality
              ? undefined
              : (suppliers?.items as (ICustomerAdminSupplier | ISupplier)[]).find(x => x.id === selectedSupplier?.id)
                  ?.isDeleted
          }
          globalDocumentType={globalDocumentTypeState}
        />
      </Box>
    </CommonPageLayout>
  )
}

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  backgroundColor: `${theme.palette.primary.main}1A`,
  borderRadius: '6px',
  padding: '3px',
  gap: '2px',
  border: `1px solid ${darken(getThemeColor(theme, MUIThemeColors.inputSearch), 0.2)}`,
  '& .MuiToggleButtonGroup-grouped': {
    border: 'none',
    borderRadius: '4px !important',
    color: `${getThemeColor(theme, MUIThemeColors.black)} !important`,
  },
  '& .Mui-selected': {
    backgroundColor: `${getThemeColor(theme, MUIThemeColors.primaryMain)} !important`,
    color: `${getThemeColor(theme, MUIThemeColors.white)} !important`,
  },
}))
